
import React, {useState, useEffect, useMemo} from 'react';
import { getAuthHeader } from '../../utils/auth';
import { useAuth } from './../auth/AuthContext';
import { useDataEngine } from '@dhis2/app-runtime';
import { CircularLoader, NoticeBox, SingleSelect } from '@dhis2/ui';
import { useParams, useSearchParams } from 'react-router-dom';
import AppraisalForm from './AppraisalForm';
import { Users, ChevronLeft, UserCircle, ChevronDown, ChevronRight } from 'lucide-react';
import './AppraisalForm.css';
import { useCurrentUserPosition } from './hooks/useCurrentUserPosition';
import { useAppraisalEvents } from './hooks/useAppraisalEvents';
import {
  APPRAISAL_PROGRAM_STAGES,
  APPRAISAL_PROGRAM,
  DATA_ELEMENTS,
  APPRAISAL_STATUSES,
} from './api/appraisalConstants';
import { POSITION_ATTRIBUTES } from '../auth/utils/reportingStructure';
import { BsExclamationTriangle } from 'react-icons/bs';

// Objective Data Elements (used for progress calculation)
const OBJECTIVE_DATA_ELEMENTS = {
  NAME: 'EuN5ztFnXsQ',
  DESCRIPTION: 'nWqW5BrcgP1',
  START_DATE: 'eDcVy661j9e',
  COMPLETION_DATE: 'J0rjxrDKn1l',
  APPROVAL_STATUS: 'DzQorireRBf',
};

// Helper to check if user is top-level (no reporting manager)
const isTopLevelUser = (currentUserPosition) => {
  if (!currentUserPosition?.attributes) return false;
  const attributes = extractAttributes(currentUserPosition.attributes);
  // Check if the user has a reporting manager
  const hasReportingTo = !!attributes[POSITION_ATTRIBUTES.REPORTING_TO];
  return !hasReportingTo;
};

// Helper to get the direct reporting chain to determine if user has approvers
const getUserApprovalHierarchy = async (engine, currentUserPosition) => {
  if (!currentUserPosition?.trackedEntityInstance) return { hasApprover: false, approverLevel: 'none' };
  
  // Check if user has a reporting manager
  const attributes = extractAttributes(currentUserPosition.attributes || []);
  const reportingTo = attributes[POSITION_ATTRIBUTES.REPORTING_TO];
  
  if (!reportingTo) {
    return { hasApprover: false, approverLevel: 'none', reason: 'top_level' };
  }
  
  return { hasApprover: true, approverLevel: 'manager', reportingToTei: reportingTo };
};

const extractAttributes = (attributes = []) => {
  const map = {};
  attributes.forEach((attr) => (map[attr.attribute] = attr.value));
  return map;
};

// ─── Progress Bar ──────────────────────────────────────────────
const ProgressBar = ({ value = 0 }) => {
  const fillClass =
    value >= 80 ? 'apr-progress-fill--high'
      : value >= 50 ? 'apr-progress-fill--mid'
        : 'apr-progress-fill--low';
  return (
    <div className="apr-progress-wrap">
      <div className="apr-progress-track">
        <div
          className={`apr-progress-fill ${fillClass}`}
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </div>
      <span className="apr-progress-pct">{value}%</span>
    </div>
  );
};

// ─── Status Chip ──────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const map = {
    approved: { label: 'Approved', cls: 'apr-chip--approved', icon: null },
    pending: { label: 'Pending', cls: 'apr-chip--pending', icon: null },
    draft: { label: 'Draft', cls: 'apr-chip--draft', icon: null },
    submitted: { label: 'Submitted', cls: 'apr-chip--submitted', icon: null },
    reviewed: { label: 'Reviewed', cls: 'apr-chip--reviewed', icon: null },
    finalized: { label: 'Finalized', cls: 'apr-chip--finalized', icon: null },
  };
  const cfg = map[status] || map['pending'];
  return (
    <span className={`apr-chip ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
};

const PerformanceAppraisalPage = ({ reportType = null, readOnlyReport = false }) => {
  // ✅ All hooks at the very top
  const { user } = useAuth();
  const { type: routeType } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [subordinates, setSubordinates] = useState([]);
  
  const subordinateTei = searchParams.get('subordinate'); 
  const engine = useDataEngine();

  const [fiscalYear, setFiscalYear] = useState({ start: '', end: '' });
  const [fiscalYearLoaded, setFiscalYearLoaded] = useState(false);
  const [availableFiscalYears, setAvailableFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);
  const [showHistoricalView, setShowHistoricalView] = useState(false);
  const [historicalAppraisalData, setHistoricalAppraisalData] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isManager, setIsManager] = useState(false);

  const [isTopLevel, setIsTopLevel] = useState(false);
  const [approvalHierarchy, setApprovalHierarchy] = useState({ hasApprover: true, approverLevel: 'manager' });
  const [expandedCards, setExpandedCards] = useState({});
  const toggleExpand = (tei) => {
    setExpandedCards(prev => ({ ...prev, [tei]: !prev[tei] }));
  };

  // Load position for current user (manager or employee)
  const { currentUserPosition, loading: positionLoading } =
    useCurrentUserPosition(user?.id, user?.organisationUnits || []);

  // ✅ Validate type
  const type = reportType || routeType;
  const isValidType = type === 'mid-year' || type === 'year-end';
  const programStage = isValidType
    ? type === 'mid-year'
      ? APPRAISAL_PROGRAM_STAGES.MID_YEAR
      : APPRAISAL_PROGRAM_STAGES.YEAR_END
    : null;

  // ✅ Determine TARGET TEI: subordinate (if viewing team) OR own position
  const targetTei = subordinateTei || currentUserPosition?.trackedEntityInstance || null;
  const targetOrgUnit = currentUserPosition?.orgUnit || null;

   const selectedSubordinate = useMemo(
      () => subordinates.find(sub => sub.tei === subordinateTei),
      [subordinates, subordinateTei]
   );

   // Helper to get the latest appraisal for a position
const fetchLatestAppraisal = async (engine, tei, orgUnit) => {
  try {
    const response = await engine.query({
      events: {
        resource: 'events',
        params: {
          program: 'yiT00XKrrHP',
          programStage: programStage, // need to pass programStage? We'll get from context
          trackedEntityInstance: tei,
          orgUnit: orgUnit,
          ouMode: 'ACCESSIBLE',
          fields: 'eventDate,status,dataValues[dataElement,value]',
          pageSize: 1,
          order: 'eventDate:desc',
        },
      },
    });
    const event = response.events?.events?.[0];
    if (event) {
      const status = event.dataValues?.find(dv => dv.dataElement === DATA_ELEMENTS.APPRAISAL_STATUS)?.value || 'draft';
      return {
        date: event.eventDate,
        status: status,
      };
    }
    return null;
  } catch (e) {
    console.warn('Failed to fetch appraisal for', tei, e);
    return null;
  }
};

// Helper to get overall objective progress for a position
const fetchObjectivesProgress = async (engine, tei, orgUnit) => {
  try {
    const response = await engine.query({
      objectives: {
        resource: 'events',
        params: {
          program: 'yiT00XKrrHP',
          programStage: 'VIBtpvkpHFR',
          orgUnit: orgUnit,
          ouMode: 'ACCESSIBLE',
          filter: [`SlgO60cMK3F:eq:${tei}`],
          fields: 'event,dataValues[dataElement,value]',
          pageSize: 50,
        },
      },
    });
    const objectives = response.objectives?.events || [];
    if (objectives.length === 0) return 0;
    // For each objective, we might have KPI data, but we can just count completed statuses
    // For simplicity, we'll just return the number of objectives with approvalStatus = 'approved'
    const approved = objectives.filter(obj => 
      obj.dataValues?.find(dv => dv.dataElement === OBJECTIVE_DATA_ELEMENTS.APPROVAL_STATUS)?.value === 'approved'
    ).length;
    return Math.round((approved / objectives.length) * 100);
  } catch (e) {
    console.warn('Failed to fetch objectives progress for', tei, e);
    return 0;
  }
};
  
  
    useEffect(() => {
      if (!user?.id || !currentUserPosition?.trackedEntityInstance) return;
      
      let cancelled = false;

      const loadSubordinates = async () => {
        try {
          // Step 1: Query positions where REPORTING_TO = manager's position TEI
          const response = await engine.query({
            subordinates: {
              resource: 'trackedEntityInstances',
              params: {
                trackedEntityType: 'UGGo0Ebjl0E',
                ou: currentUserPosition?.orgUnit,
                ouMode: 'ACCESSIBLE',
                filter: `${POSITION_ATTRIBUTES.REPORTING_TO}:eq:${currentUserPosition.trackedEntityInstance}`,
                fields: 'trackedEntityInstance,attributes[attribute,value]'
              }
            }
          });
          
          // Step 2: Map positions with incumbent info
          const subs = response.subordinates?.trackedEntityInstances?.map(tei => {
            const attrs = extractAttributes(tei.attributes || []);
            return {
              tei: tei.trackedEntityInstance,
              positionTitle: attrs[POSITION_ATTRIBUTES.POSITION_TITLE] || 'Unknown Position',
              incumbentId: attrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT]
            };
          }) || [];

          // Step 3: Fetch user names and additional data
          const subsWithData = await Promise.all(subs.map(async sub => {
            let displayName = sub.positionTitle;
            let lastAppraisal = null;
            let objectivesProgress = 0;

            // Fetch user name
            if (sub.incumbentId) {
              try {
                const userRes = await fetch(`${window.location.origin}/api/users/${sub.incumbentId}`, {
                  headers: {
                    ...getAuthHeader(),  
                    'Content-Type': 'application/json'
                  }
                });
                if (userRes.ok) {
                  const userData = await userRes.json();
                  displayName = userData.displayName || userData.name || sub.incumbentId;
                }
              } catch (err) {
                console.warn('Failed to fetch user name for', sub.incumbentId, err);
              }
            }

            // Fetch latest appraisal (use the current programStage from the page)
            const appraisalData = await fetchLatestAppraisal(engine, sub.tei, currentUserPosition?.orgUnit);
            if (appraisalData) {
              lastAppraisal = {
                date: appraisalData.date,
                status: appraisalData.status,
              };
            }

            // Fetch objectives progress
            const progress = await fetchObjectivesProgress(engine, sub.tei, currentUserPosition?.orgUnit);
            objectivesProgress = progress;

            return {
              ...sub,
              displayName: displayName,
              lastAppraisal: lastAppraisal,
              objectivesProgress: objectivesProgress,
            };
          }));
          
          console.log('👥 Subordinates loaded with data:', subsWithData);
          if (!cancelled) {
            setSubordinates(subsWithData);
          }
        } catch (err) {
          console.error('Failed to load subordinates:', err);
          if (!cancelled) {
            setSubordinates([]);
          }
        }
      };

      loadSubordinates();
      return () => { cancelled = true; };
    }, [user?.id, currentUserPosition, engine]);


   useEffect(() => {
      if (!targetTei || !programStage) return;

      let cancelled = false;
      
      const loadFiscalYearsFromAppraisals = async () => {
        try {
          // Query all appraisal events to get distinct fiscal years
          const response = await engine.query({
            events: {
              resource: 'events',
              params: {
                program: APPRAISAL_PROGRAM,
                programStage: programStage,
                trackedEntityInstance: targetTei,
                fields: 'eventDate,event',
                pageSize: 200,
                ouMode: 'ACCESSIBLE'
              }
            }
          });
          
          const events = response.events?.events || [];
          
          // Extract unique years from event dates
          const uniqueYears = new Set();
          events.forEach(event => {
            if (event.eventDate) {
              const eventYear = new Date(event.eventDate).getFullYear();
              uniqueYears.add(eventYear);
            }
          });
          
          // Also include current fiscal year based on date
          const now = new Date();
          const currentYear = now.getFullYear();
          const julyFirstCurrentYear = new Date(currentYear, 6, 1);
          let currentFiscalStartYear = now >= julyFirstCurrentYear ? currentYear : currentYear - 1;
          uniqueYears.add(currentFiscalStartYear);
          
          // Sort years in descending order
          const sortedYears = Array.from(uniqueYears).sort((a, b) => b - a);
          
          // Build fiscal year list
          const fiscalYearsList = sortedYears.map(year => ({
            year: year,
            start: `${year}-07-01`,
            end: `${year + 1}-06-30`,
            label: `${year} - ${year + 1}`,
            isCurrent: year === currentFiscalStartYear
          }));
          
          if (!cancelled) {
            setAvailableFiscalYears(fiscalYearsList);
            console.log('✅ Generated appraisal fiscal years:', fiscalYearsList);
          }
          
        } catch (error) {
          console.error('Failed to load appraisal fiscal years:', error);
          if (!cancelled) {
            setAvailableFiscalYears([]);
          }
        }
      };
      
      if (targetTei && programStage) {
        loadFiscalYearsFromAppraisals();
      }

      return () => { cancelled = true; };
   }, [targetTei, programStage, engine]);

   // Load current fiscal year configuration from metadata
      useEffect(() => {
        const orgUnitId = user?.organisationUnits?.[0]?.id;
        
        if (!orgUnitId || !engine || fiscalYearLoaded) return;

        let cancelled = false;

        const loadFiscalYear = async () => {
          try {
            console.log('🔍 Loading fiscal year for orgUnit:', orgUnitId);
            
            const response = await engine.query({
              events: {
                resource: 'events',
                params: {
                  program: 'NkReudUAW4F',
                  programStage: 'htIWgNQWEMm',
                  ou: orgUnitId,
                  ouMode: 'ACCESSIBLE',
                  pageSize: 1,
                  order: 'eventDate:desc',
                  fields: 'event,dataValues[dataElement,value]'
                }
              }
            });
            
            const event = response.events?.events?.[0];
            let startPattern = '', endPattern = '';
            
            if (event) {
              event.dataValues?.forEach(dv => {
                if (dv.dataElement === 'U55MqwBcRAe') startPattern = dv.value;
                if (dv.dataElement === 'kgdk4NXu4f5') endPattern = dv.value;
              });
            }
            
            if (startPattern && endPattern) {
              const startDate = new Date(startPattern);
              const endDate = new Date(endPattern);
              const now = new Date();
              const currentYear = now.getFullYear();
              
              // For July 1 start, fiscal year runs from July 1 to June 30
              let fiscalStartDate, fiscalEndDate;
              const julyFirstCurrentYear = new Date(currentYear, 6, 1);
              
              if (now >= julyFirstCurrentYear) {
                fiscalStartDate = new Date(currentYear, startDate.getMonth(), startDate.getDate());
                fiscalEndDate = new Date(currentYear + 1, endDate.getMonth(), endDate.getDate());
              } else {
                fiscalStartDate = new Date(currentYear - 1, startDate.getMonth(), startDate.getDate());
                fiscalEndDate = new Date(currentYear, endDate.getMonth(), endDate.getDate());
              }
              
              const formattedStart = `${fiscalStartDate.getFullYear()}-${String(fiscalStartDate.getMonth() + 1).padStart(2, '0')}-${String(fiscalStartDate.getDate()).padStart(2, '0')}`;
              const formattedEnd = `${fiscalEndDate.getFullYear()}-${String(fiscalEndDate.getMonth() + 1).padStart(2, '0')}-${String(fiscalEndDate.getDate()).padStart(2, '0')}`;
              
              if (!cancelled) {
                setFiscalYear({ start: formattedStart, end: formattedEnd });
              }
            } else {
              const now = new Date();
              const currentYear = now.getFullYear();
              const defaultStart = now >= new Date(currentYear, 6, 1) 
                ? `${currentYear}-07-01` 
                : `${currentYear - 1}-07-01`;
              const defaultEnd = now >= new Date(currentYear, 6, 1)
                ? `${currentYear + 1}-06-30`
                : `${currentYear}-06-30`;
              if (!cancelled) {
                setFiscalYear({ start: defaultStart, end: defaultEnd });
              }
            }
            if (!cancelled) {
              setFiscalYearLoaded(true);
            }
          } catch (error) {
            console.error('❌ Failed to load fiscal year:', error);
            if (!cancelled) {
              const now = new Date();
              const currentYear = now.getFullYear();
              setFiscalYear({ 
                start: `${currentYear - 1}-07-01`, 
                end: `${currentYear}-06-30` 
              });
              setFiscalYearLoaded(true);
            }
          }
        };
        
        loadFiscalYear();
        return () => { cancelled = true; };
    }, [user?.organisationUnits?.[0]?.id, engine, fiscalYearLoaded]);

    // Detect if current user is top-level (CEO) and should bypass approval
    useEffect(() => {
      let cancelled = false;

      const checkApprovalHierarchy = async () => {
        if (!currentUserPosition) return;
        
        const topLevel = isTopLevelUser(currentUserPosition);
        if (!cancelled) {
          setIsTopLevel(topLevel);
        }
        
        if (topLevel) {
          console.log('👑 Top-level user detected (CEO). Approval flow will be bypassed.');
          if (!cancelled) {
            setApprovalHierarchy({ hasApprover: false, approverLevel: 'top_level', reason: 'CEO - No approver' });
          }
        } else {
          // For non-top-level users, check if they have an approver
          const hierarchy = await getUserApprovalHierarchy(engine, currentUserPosition);
          if (!cancelled) {
            setApprovalHierarchy(hierarchy);
            console.log('📋 User approval hierarchy:', hierarchy);
          }
        }
      };
      
      if (currentUserPosition && engine) {
        checkApprovalHierarchy();
      }

      return () => { cancelled = true; };
    }, [currentUserPosition, engine]);

    const loadHistoricalAppraisal = async (year) => {
        setLoading(true);
        try {
          const fiscalConfig = availableFiscalYears.find(fy => fy.year === year);
          if (!fiscalConfig) return;
          
          // Query appraisal events for the selected fiscal year
          const response = await engine.query({
            events: {
              resource: 'events',
              params: {
                program: APPRAISAL_PROGRAM,
                programStage: programStage,
                trackedEntityInstance: targetTei,
                fields: 'event,dataValues[dataElement,value],status',
                filter: `eventDate:ge:${fiscalConfig.start}:eventDate:le:${fiscalConfig.end}`,
                pageSize: 1,
                ouMode: 'ACCESSIBLE'
              }
            }
          });
          
          const event = response.events?.events?.[0];
          if (event) {
            const dv = (de) => event.dataValues?.find(d => d.dataElement === de)?.value || '';
            const rawStatus = dv(DATA_ELEMENTS.APPRAISAL_STATUS);
            
            setHistoricalAppraisalData({
              employeeSelfRatingTask: dv(DATA_ELEMENTS.EMPLOYEE_SELF_RATING_TASK),
              employeeSelfRatingBehavioural: dv(DATA_ELEMENTS.EMPLOYEE_SELF_RATING_BEHAVIOURAL),
              employeeSelfComments: dv(DATA_ELEMENTS.EMPLOYEE_COMMENTS),
              managerRatingTask: dv(DATA_ELEMENTS.MANAGER_RATING_TASK),
              managerRatingBehavioural: dv(DATA_ELEMENTS.MANAGER_RATING_BEHAVIOURAL),
              managerComments: dv(DATA_ELEMENTS.MANAGER_COMMENTS),
              appraisalStatus: rawStatus ? rawStatus.toLowerCase() : APPRAISAL_STATUSES.DRAFT,
              eventId: event.event,
            });
            setShowHistoricalView(true);
            setSelectedFiscalYear(year);
          } else {
            // No appraisal found for that year
            setHistoricalAppraisalData(null);
            setShowHistoricalView(true);
            setSelectedFiscalYear(year);
          }
        } catch (error) {
          console.error('Failed to load historical appraisal:', error);
          setError('Could not load historical data');
        } finally {
          setLoading(false);
        }
   };

  
  // Load appraisal event for TARGET TEI (critical: use targetTei, not positionTEI)
  const { appraisalEvent, loading: eventLoading, refetch } = useAppraisalEvents(
    targetTei,              // ← Use targetTei (subordinate or self)
    programStage,
    targetOrgUnit,          // ← Use targetOrgUnit
    engine
  );

  const TeamMemberCards = ({ subordinates, selectedTei, onSelect }) => {
    return (
      <div className="team-member-cards" style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
        {subordinates.map(sub => {
          const isSelected = sub.tei === selectedTei;
          const isExpanded = expandedCards[sub.tei] || false;
          return (
            <div
              key={sub.tei}
              onClick={() => onSelect(sub.tei)}
              style={{
                border: `2px solid ${isSelected ? '#006b9e' : '#e0e0e0'}`,
                borderRadius: '8px',
                padding: '12px 16px',
                cursor: 'pointer',
                backgroundColor: isSelected ? '#f0f7ff' : '#fff',
                transition: 'all 0.2s',
                boxShadow: isSelected ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <UserCircle size={28} color="#006b9e" />
                  <div>
                    <span style={{ fontWeight: 600, fontSize: '1rem' }}>
                      {sub.displayName || sub.positionTitle}
                    </span>
                    {sub.lastAppraisal?.status === 'submitted' && (
                      <span style={{ 
                        background: '#f39c12', 
                        color: 'white', 
                        borderRadius: '99px', 
                        padding: '0 8px', 
                        fontSize: '0.6rem', 
                        fontWeight: 700,
                        lineHeight: '1.8',
                      }}>
                        Pending Review
                      </span>
                    )}
                    <div style={{ fontSize: '0.85rem', color: '#555' }}>
                      {sub.positionTitle}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {/* You can add a status badge here if you fetch it */}
                  <span style={{ fontSize: '0.75rem', background: '#e8f0fe', padding: '2px 8px', borderRadius: '12px' }}>
                    {isExpanded ? 'Expanded' : 'Click to select'}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleExpand(sub.tei); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                </div>
              </div>
              {isExpanded && (
                  <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #eee', fontSize: '0.9rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      {sub.lastAppraisal ? (
                        <>
                          <div>
                            <strong>Last Appraisal:</strong>
                            <div style={{ fontSize: '0.85rem', color: '#555' }}>
                              {new Date(sub.lastAppraisal.date).toLocaleDateString()}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#555' }}>
                              Status: <StatusChip status={sub.lastAppraisal.status} />
                            </div>
                          </div>
                          <div>
                            <strong>Objectives Progress:</strong>
                            <div style={{ fontSize: '0.85rem', color: '#555' }}>
                              {sub.objectivesProgress}% completed
                            </div>
                            <ProgressBar value={sub.objectivesProgress} />
                          </div>
                        </>
                      ) : (
                        <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#888', fontSize: '0.85rem' }}>
                          No previous appraisal found.
                        </div>
                      )}
                    </div>
                  </div>
                )}
            </div>
          );
        })}
      </div>
    );
  };

  {/* Fiscal Year Selector - Show for managers and admins */}
  {((isManager && subordinates.length > 0) || !readOnlyReport) && availableFiscalYears.length > 0 && (
    <div style={{ 
      marginBottom: '24px', 
      padding: '16px', 
      backgroundColor: '#f0f4f8', 
      borderRadius: '8px',
      border: '1px solid #d0d7de'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <label style={{ fontWeight: 600, marginRight: '12px' }}>Appraisal Period:</label>
          <span style={{ 
            padding: '4px 12px', 
            backgroundColor: fiscalYear.start && fiscalYear.end ? '#4caf50' : '#ff9800',
            color: 'white',
            borderRadius: '4px',
            fontWeight: 500
          }}>
            {fiscalYear.start && fiscalYear.end 
              ? `${new Date(fiscalYear.start).getFullYear()} - ${new Date(fiscalYear.end).getFullYear()}`
              : 'Not Configured'}
          </span>
        </div>
        
        {availableFiscalYears.length > 0 && (
          <div>
            <label style={{ fontWeight: 600, marginRight: '12px' }}>View Historical:</label>
            <SingleSelect
                label="Historical Appraisal"
                selected={selectedFiscalYear ? String(selectedFiscalYear) : ''}
                options={[
                  { value: '', label: '-- Current Year --' },
                  ...availableFiscalYears
                    .filter(fy => !fy.isCurrent)
                    .map(fy => ({ value: String(fy.year), label: fy.label }))
                ]}
                onChange={({ selected }) => {
                  const yearValue = selected ? parseInt(selected) : null;
                  if (yearValue) {
                    const yearData = availableFiscalYears.find(y => y.year === yearValue);
                    if (yearData) {
                      if (yearData.isCurrent) {
                        setSelectedFiscalYear(null);
                        setShowHistoricalView(false);
                        setHistoricalAppraisalData(null);
                        // Refetch current data without reload
                        refetch(); // from useAppraisalEvents
                      } else {
                        loadHistoricalAppraisal(yearData.year);
                      }
                    }
                  } else {
                    setSelectedFiscalYear(null);
                    setShowHistoricalView(false);
                    setHistoricalAppraisalData(null);
                    refetch();
                  }
                }}
                dense
                width="220px"
              />
          </div>
        )}
      </div>
      
      {showHistoricalView && selectedFiscalYear && (
        <div style={{
          marginTop: '12px',
          padding: '8px 12px',
          backgroundColor: '#fff3e0',
          borderRadius: '4px',
          fontSize: '0.9rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>📅 Viewing historical data for fiscal year {selectedFiscalYear} - {selectedFiscalYear + 1}</span>
          <button
            onClick={() => {
              setSelectedFiscalYear(null);
              setShowHistoricalView(false);
              setHistoricalAppraisalData(null);
              refetch();
            }}
            style={{
              padding: '4px 12px',
              backgroundColor: '#ff9800',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Back to Current
          </button>
        </div>
      )}
    </div>
  )}

  // UI States

  if (!isValidType) {
    return (
      <div className="apr-page-wrap">
        <NoticeBox error title="Invalid Appraisal Type">
          Please select a valid appraisal type:
          <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
            <li><a href="/performance-appraisal/mid-year">Mid-Year Appraisal</a></li>
            <li><a href="/performance-appraisal/year-end">Year-End Appraisal</a></li>
          </ul>
        </NoticeBox>
      </div>
    );
  }

  if (positionLoading) {
    return (
      <div className="apr-loading">
        <CircularLoader small />
        <span>Loading your position…</span>
      </div>
    );
  }

  if (!currentUserPosition) {
    return (
      <div className="apr-page-wrap">
        <div className="apr-no-position">
          <h4><span><BsExclamationTriangle size={14}/></span> No Position Assigned</h4>
          <p>Your account (<strong>{user?.username}</strong>) is not linked to any position in the system.</p>
          <p><strong>Name:</strong> {user?.name}</p>
          <p><strong>Organisation:</strong> {user?.organisationUnits?.map(ou => ou.name).join(', ') || 'None'}</p>
        </div>
      </div>
    );
  }

  if (eventLoading) {
    return (
      <div className="apr-loading">
        <CircularLoader small />
        <span>Loading appraisal data…</span>
      </div>
    );
  }

  const isMidYear = type === 'mid-year';
  

  // Render form with TARGET TEI (subordinate or self)
  return (
    <div className="apr-page-wrap">

      {/* ── Page Top Bar ──────────────────────────────────────── */}
      <div className="apr-page-top">
        <div>
          <h2>
            {isMidYear ? 'Mid-Year' : 'Year-End'} Performance Appraisal
          </h2>
          <div className="apr-page-subtitle">
            {subordinateTei
              ? `Reviewing: ${selectedSubordinate?.displayName || selectedSubordinate?.positionTitle || 'Team Member'}`
              : 'Reviewing your own appraisal'}
          </div>
        </div>
        <span className={`apr-type-badge ${isMidYear ? 'apr-type-badge--mid' : 'apr-type-badge--year'}`}>
          {isMidYear ? '🗓 Mid-Year' : '📅 Year-End'}
        </span>
      </div>

      {/* ── Subordinate Selector ─────────────────────────────── */}
      {!readOnlyReport && subordinates.length > 0 && (
        <div className="apr-selector-bar" data-print-hidden="true">
          <span className="apr-selector-icon"><Users size={18} /></span>
          <label className="apr-selector-label">Review Team Member:</label>
          <TeamMemberCards
            subordinates={subordinates}
            selectedTei={subordinateTei}
            onSelect={(tei) => {
              const newParams = new URLSearchParams(searchParams);
              if (tei) {
                newParams.set('subordinate', tei);
              } else {
                newParams.delete('subordinate');
              }
              setSearchParams(newParams);  // we'll use setSearchParams
            }}
          />
        </div>
      )}

      {/* ── Context Banner (when viewing a subordinate) ───────── */}
      {!readOnlyReport && subordinateTei && selectedSubordinate && (
          <div className="apr-context-banner">
            <span className="apr-context-banner-icon"><UserCircle size={20} /></span>
            <div>
              <div>
                Viewing appraisal for{' '}
                <span className="apr-context-banner-name">
                  {selectedSubordinate.displayName || selectedSubordinate.positionTitle || 'Team Member'}
                </span>
              </div>
              {selectedSubordinate.positionTitle && selectedSubordinate.displayName && (
                <div className="apr-context-banner-id">
                  Position: {selectedSubordinate.positionTitle}
                </div>
              )}
            </div>
            <button
              className="apr-back-link apr-btn apr-btn--primary"
              data-print-hidden="true"
              onClick={() => {
                window.history.replaceState({}, '', window.location.pathname);
                setSearchParams({}); 
                refetch();
              }}
            >
              <ChevronLeft size={14} />
              Back to my appraisal
            </button>
          </div>
        )}

      {/* ── Appraisal Form ───────────────────────────────────── */}
      <AppraisalForm
        positionTEI={targetTei}
        programStage={programStage}
        orgUnit={targetOrgUnit}
        readOnlyReport={readOnlyReport || showHistoricalView} // Make read-only for historical view
        appraisalEvent={appraisalEvent}
        appraisalProgram={APPRAISAL_PROGRAM}
        dataElements={DATA_ELEMENTS}
        appraisalStatusOptions={APPRAISAL_STATUSES}
        onSaved={refetch}
        isHistoricalView={showHistoricalView}
        isTopLevelUser={isTopLevel}
        approvalHierarchy={approvalHierarchy}
        fiscalYearStart={fiscalYear.start}
        fiscalYearEnd={fiscalYear.end}
        selectedFiscalYear={selectedFiscalYear}   // if not null, use that year instead
        availableFiscalYears={availableFiscalYears}
      />
    </div>
  );
};

export default PerformanceAppraisalPage;
